import React, { useRef } from "react";
import { Box, Text, Textarea, Wrap, WrapItem, Tag, Divider } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";
import { renderTemplatePreview } from "utils/templatePreview";

const PLACEHOLDERS = ["title", "header", "text_preview", "link", "category", "date"];

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

// Shared by CreateBotTemplate and the edit modal in ListBotTemplate so the
// placeholder legend + live preview behave identically in both places.
function TemplateContentEditor({ value, onChange, isInvalid, errorMessage }) {
  const { t } = useTranslation();
  const textareaRef = useRef(null);

  const insertPlaceholder = (placeholder) => {
    const textarea = textareaRef.current;
    const token = `{${placeholder}}`;
    if (!textarea) {
      onChange(`${value || ""}${token}`);
      return;
    }
    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
    onChange(next);
    // Restore focus + cursor position after the inserted token on the next tick.
    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + token.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <Box mt="16px">
      <Text fontSize="sm" fontWeight="500" color={GOLD_TEXT} mb="2">
        {t("Content")}
      </Text>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        fontFamily="mono"
        isInvalid={isInvalid}
        {...fieldProps}
      />
      {errorMessage && (
        <Text color="red.500" fontSize="xs" mt="4px">
          {errorMessage}
        </Text>
      )}

      <Wrap mt="10px" spacing="6px">
        {PLACEHOLDERS.map((placeholder) => (
          <WrapItem key={placeholder}>
            <Tag
              size="sm"
              cursor="pointer"
              bg={GOLD_LIGHT}
              color={GOLD_TEXT}
              fontFamily="mono"
              _hover={{ bg: GOLD_BORDER, color: "#1A1A1A" }}
              onClick={() => insertPlaceholder(placeholder)}
            >
              {`{${placeholder}}`}
            </Tag>
          </WrapItem>
        ))}
      </Wrap>
      <Text fontSize="xs" color="gray.500" mt="4px">
        {t("Click a placeholder to insert it. Missing values render empty and extra blank lines collapse automatically.")}
      </Text>

      <Divider my="14px" />

      <Text fontSize="sm" fontWeight="500" color={GOLD_TEXT} mb="2">
        {t("Live Preview")}
      </Text>
      <Box
        bg={GOLD_LIGHT}
        border="1px solid"
        borderColor={GOLD_BORDER}
        borderRadius="md"
        p="12px"
        whiteSpace="pre-wrap"
        fontSize="sm"
        minH="60px"
      >
        {renderTemplatePreview(value) || t("(empty)")}
      </Box>
    </Box>
  );
}

export default TemplateContentEditor;
